import React, { useEffect, useState } from "react";

interface TextProps {
  x: number;
  y: number;
  width: number;
  height: number;
  children: string;
  style?: React.CSSProperties;
  lineHeight?: number;
  textAnchor?: "start" | "middle" | "end";
  fontFamily?: string;
  color?: string;
}

interface MeasuredLine {
  text: string;
  /** Precomputed x offset implementing the text anchor. */
  offset: number;
}

/**
 * SVG text with word wrapping. Measurement is expensive (it renders probe
 * <text> nodes into the live DOM), so it runs in a single effect keyed by
 * the actual inputs — including a serialized style, since callers pass
 * fresh style objects every render — and produces lines with their anchor
 * offsets precomputed. Render itself does no DOM measurement.
 */
export const MultilineSvgText: React.FC<TextProps> = ({
  x,
  y,
  width,
  height,
  children,
  style = {},
  // Konva.Text's default — the editor and the viewer must produce the
  // same line spacing or saved text blocks clip/shift between the two.
  lineHeight = 1,
  textAnchor = "start",
  fontFamily,
  color
}) => {
  const [measuredLines, setMeasuredLines] = useState<MeasuredLine[]>([]);

  // Extract fontSize in pixels from style
  const fontSizePx =
    typeof style.fontSize === "string"
      ? parseFloat(style.fontSize)
      : (style.fontSize as number) || 16;

  // Callers build the style object inline, so its identity changes every
  // render — key the measurement effect on its content instead.
  const styleKey = JSON.stringify(style);

  useEffect(() => {
    let cancelled = false;

    const compute = () => {
      // One probe <text> shared by the whole measurement pass.
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      const text = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      Object.assign(text.style, style);
      if (fontFamily) text.setAttribute("font-family", fontFamily);
      svg.appendChild(text);
      document.body.appendChild(svg);

      const measure = (value: string): number => {
        text.textContent = value;
        return text.getComputedTextLength();
      };

      // Split by newlines first, then by whitespace within each line.
      // Insert a special "\n" token to force line breaks during layout.
      const tokens: string[] = [];
      const paragraphs = children.split("\n");
      paragraphs.forEach((paragraph, pIdx) => {
        if (pIdx > 0) tokens.push("\n");
        const words = paragraph.split(/\s+/).filter(Boolean);
        tokens.push(...words);
      });

      const spaceWidth = measure(" ");

      const lines: string[][] = [];
      let currentLine: string[] = [];
      let currentLineWidth = 0;

      for (const word of tokens) {
        // Forced line break
        if (word === "\n") {
          lines.push(currentLine);
          currentLine = [];
          currentLineWidth = 0;
          continue;
        }

        const wordWidth = measure(word);
        const space = currentLine.length > 0 ? spaceWidth : 0;

        if (currentLineWidth + space + wordWidth <= width) {
          currentLine.push(word);
          currentLineWidth += space + wordWidth;
        } else if (wordWidth <= width) {
          if (currentLine.length) lines.push(currentLine);
          currentLine = [word];
          currentLineWidth = wordWidth;
        } else {
          // Word is too long — break it letter by letter
          if (currentLine.length) {
            lines.push(currentLine);
            currentLine = [];
            currentLineWidth = 0;
          }

          let partial = "";
          let partialWidth = 0;
          for (const char of word) {
            const charWidth = measure(char);
            if (partialWidth + charWidth > width) {
              lines.push([partial]);
              partial = char;
              partialWidth = charWidth;
            } else {
              partial += char;
              partialWidth += charWidth;
            }
          }
          if (partial) {
            currentLine = [partial];
            currentLineWidth = partialWidth;
          }
        }
      }

      if (currentLine.length) lines.push(currentLine);

      const anchorOffset = (lineWidth: number): number => {
        switch (textAnchor) {
          case "middle":
            return lineWidth / 2 - width / 2;
          case "end":
            return lineWidth - width;
          default:
            return 0;
        }
      };

      const result = lines.map((words) => {
        const lineText = words.join(" ");
        return {
          text: lineText,
          offset: textAnchor === "start" ? 0 : anchorOffset(measure(lineText))
        };
      });

      document.body.removeChild(svg);
      if (!cancelled) setMeasuredLines(result);
    };

    compute();

    // Re-measure once fonts finish loading so widths are correct.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) compute();
      });
    }

    return () => {
      cancelled = true;
    };
    // styleKey stands in for the style object's content.
  }, [children, width, textAnchor, fontFamily, styleKey]);

  // Max number of lines that fit in the height, using actual font size and line height
  const lineHeightPx = fontSizePx * lineHeight;
  const maxLines = Math.max(1, Math.floor(height / lineHeightPx));

  // Vertical placement mirrors Konva.Text: each line's glyphs are centered
  // in their line box (canvas textBaseline "middle" ≙ SVG dominant-baseline
  // "central"), line i's box spanning [i, i+1] × lineHeightPx from the top.
  return (
    <g transform={`translate(${x}, ${y})`}>
      <text style={{ ...style, dominantBaseline: "central" }} color={color}>
        {measuredLines.slice(0, maxLines).map((line, index) => (
          <tspan
            // biome-ignore lint/suspicious/noArrayIndexKey: lines have no ids; index is their identity
            key={index}
            x={-line.offset}
            y={(index + 0.5) * lineHeightPx}
            style={{ fontFamily, color }}
            color={color}
          >
            {line.text}
          </tspan>
        ))}
      </text>
    </g>
  );
};
